import { Injectable } from '@angular/core';
import { PaymentOptionConfig } from './whitelabel-config.model';
import { WhitelabelConfigStore } from './whitelabel-config.store';

export interface EnabledPaymentOption {
  name: string;
  /** Provider and currency form one unique v2 selection, e.g. airtel:UGX. */
  formValue: string;
  paymentOption: 'mpesa' | 'airtel' | 'mtn';
  logo: string;
  /** ISO country the provider settles in. Drives which dial code/currency apply. */
  country: 'KE' | 'UG';
  currency: 'KSH' | 'UGX';
  dialCode: '254' | '256';
  extraCharge: number;
}

export interface CurrencyAwarePaymentOption {
  payment_option: string;
  currency: 'KSH' | 'KES' | 'UGX';
  extra_charge: number;
}

const EXISTING_PAYMENT_INTEGRATIONS: Record<string, Omit<EnabledPaymentOption, 'name' | 'formValue' | 'extraCharge'>> = {
  mpesa: {
    paymentOption: 'mpesa',
    logo: '/assets/img/company1/mpesa-logo.png',
    country: 'KE',
    currency: 'KSH',
    dialCode: '254'
  },
  mobile: {
    paymentOption: 'mpesa',
    logo: '/assets/img/company1/mpesa-logo.png',
    country: 'KE',
    currency: 'KSH',
    dialCode: '254'
  },
  niobi_mtn: {
    paymentOption: 'mtn',
    logo: '/assets/img/company1/mtn-logo.jpg',
    country: 'UG',
    currency: 'UGX',
    dialCode: '256'
  },
  mtn: {
    paymentOption: 'mtn',
    logo: '/assets/img/company1/mtn-logo.jpg',
    country: 'UG',
    currency: 'UGX',
    dialCode: '256'
  },
  airtel_ke: {
    paymentOption: 'airtel',
    logo: '/assets/img/company1/airtrel.jpg',
    country: 'KE',
    currency: 'KSH',
    dialCode: '254'
  },
  airtel_ug: {
    paymentOption: 'airtel',
    logo: '/assets/img/company1/airtrel.jpg',
    country: 'UG',
    currency: 'UGX',
    dialCode: '256'
  }
};

// Providers shown per currency in the "pay in KE / UG" picker, in display
// order - Mpesa before Airtel for KE, MTN before Airtel for UG. Sourced
// from EXISTING_PAYMENT_INTEGRATIONS so the logos always match what
// mapOption()/mapCurrencyAwareOption() actually render for that provider.
const CURRENCY_PROVIDER_ORDER: Record<'KSH' | 'UGX', string[]> = {
  KSH: ['mpesa', 'airtel_ke'],
  UGX: ['niobi_mtn', 'airtel_ug']
};

@Injectable({ providedIn: 'root' })
export class PaymentOptionsService {
  constructor(private store: WhitelabelConfigStore) {}

  /** Provider logos to preview beside a currency's country code, e.g. Mpesa + Airtel for KE. */
  logosForCurrency(currency: 'KSH' | 'UGX'): string[] {
    return CURRENCY_PROVIDER_ORDER[currency]
      .map(key => EXISTING_PAYMENT_INTEGRATIONS[key]?.logo)
      .filter((logo): logo is string => !!logo);
  }

  get enabled(): EnabledPaymentOption[] {
    const configured = this.store.snapshot.config?.paymentOptions || [];
    return configured
      .map(option => this.mapOption(option))
      .filter((option): option is EnabledPaymentOption => !!option);
  }

  enabledFor(apiPaymentOptions: unknown): EnabledPaymentOption[] {
    if (!Array.isArray(apiPaymentOptions)) {
      return [];
    }

    const allowed = new Set(
      apiPaymentOptions
        .map(value => String(value || '').trim().toLowerCase())
        .filter(Boolean)
    );

    return (this.store.snapshot.config?.paymentOptions || [])
      .filter(option => allowed.has(option.value.trim().toLowerCase()))
      .map(option => this.mapOption(option))
      .filter((option): option is EnabledPaymentOption => !!option);
  }

  fromCurrencyAwareResponse(
    apiPaymentOptions: CurrencyAwarePaymentOption[],
    selectedCurrency: 'KSH' | 'UGX'
  ): EnabledPaymentOption[] {
    if (!Array.isArray(apiPaymentOptions)) {
      return [];
    }

    return apiPaymentOptions
      .map(option => this.mapCurrencyAwareOption(option))
      .filter((option): option is EnabledPaymentOption =>
        !!option && option.currency === selectedCurrency
      );
  }

  private mapOption(option: PaymentOptionConfig): EnabledPaymentOption | null {
    const integration = EXISTING_PAYMENT_INTEGRATIONS[option.value.trim().toLowerCase()];
    return integration ? {
      name: option.name,
      ...integration,
      formValue: `${integration.paymentOption}:${integration.currency}`,
      extraCharge: 0
    } : null;
  }

  private mapCurrencyAwareOption(option: CurrencyAwarePaymentOption): EnabledPaymentOption | null {
    const paymentOption = String(option?.payment_option || '').trim().toLowerCase();
    const currency = String(option?.currency || '').toUpperCase() === 'KES'
      ? 'KSH'
      : String(option?.currency || '').toUpperCase();
    const configValue = paymentOption === 'airtel'
      ? (currency === 'UGX' ? 'airtel_ug' : 'airtel_ke')
      : paymentOption === 'mtn' ? 'niobi_mtn' : paymentOption;
    const configured = (this.store.snapshot.config?.paymentOptions || []).find(
      candidate => candidate.value.trim().toLowerCase() === configValue
    );
    const integration = EXISTING_PAYMENT_INTEGRATIONS[configValue];

    if (!integration || integration.currency !== currency) {
      return null;
    }

    const fallbackName = paymentOption === 'mpesa'
      ? 'M-PESA'
      : paymentOption === 'mtn'
        ? 'MTN Mobile Money'
        : currency === 'UGX' ? 'Airtel Money (Uganda)' : 'Airtel Money (Kenya)';

    return {
      name: configured?.name || fallbackName,
      ...integration,
      formValue: `${integration.paymentOption}:${integration.currency}`,
      extraCharge: Number(option.extra_charge) || 0
    };
  }
}
